<template>
  <stormy-single-dialog
    :show="show"
    :title="title"
    :overflow="false"
    width="xl"
    @hide="onClose"
  >
    <template #title>
      <div class="flex items-center">
        <div class="text-lg flex-none font-medium leading-6 text-gray-900  w-64">
          {{ title }}
        </div>
        <div class="pl-3 flex items-center space-x-1">
          <div class="flex items-center space-x-1">
            <stormy-menu
              ref="menu"
              origin="top"
            >
              <template #trigger>
                <stormy-icon-button
                  icon="tabler-folder"
                  tooltip="Ordner wechseln"
                  class="hidden"
                />
              </template>

              <template
                #header
              >
                <div
                  class="flex items-center"
                >
                  <span class="flex-1 font-medium">Ordner wechseln</span>
                </div>
              </template>
              <div>
                <stormy-nav-group>
                  <stormy-nav-item
                    name="Unterkünfte"
                    icon="tabler-address-book"
                  >
                    <stormy-sub-nav-item name="Ferienhaus Oktavia" />
                  </stormy-nav-item>
                </stormy-nav-group>
              </div>
            </stormy-menu>

            <stormy-icon-button
              class="hidden"
              icon="layout-grid"
              size="md"
              variant="ghost"
            />
            <stormy-icon-button
              icon="layout-list"
              size="md"
              class="hidden"
              variant="ghost"
            />
            <stormy-icon-button
              icon="photo-shield"
              size="md"
              class="hidden"
              variant="ghost"
            />
          </div>
          <div class="pl-6 flex items-center space-x-1">
            <stormy-icon-button
              v-if="multiple"
              :disabled="!canSelectAll"
              icon="select-all"
              size="md"
              class="text-blue-500"
              variant="ghost"
              tooltip="Alle auswählen"
              @click="onSelectAll"
            />

            <stormy-icon-button
              v-if="multiple"
              :disabled="!canSelectNone"
              icon="deselect"
              size="md"
              class="text-blue-500"
              variant="ghost"
              tooltip="Auswahl zurücksetzen"
              @click="onSelectNone"
            />
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <div class="flex flex-1 overflow-hidden">
        <div class="flex flex-1">
          <MediaModalUpload
            :parent="parent"
            :parent-id="parentId"
            :multiple="multiple"
          />
        </div>
        <div
          v-if="currentMedia"
          class="flex-none w-80 flex bg-gray-50 flex-col border-l -mt-1 hidden"
        >
          <MediaDetails />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex-1 flex items-center w-full">
        <div class="flex-none">
          <label
            for="file-input-button"
            class="flex w-full cursor-pointer appearance-none"
          >

            <input
              id="file-input-button"
              class="w-0 h-0"
              type="file"
              multiple
              @change="onInputChange"
            >
            <MediaUploadButton />
          </label>
        </div>
        <div class="flex-1 space-x-2 text-sm text-gray-500 pl-2">
          Zum Upload Button klicken oder Bilder (gif jpg jpeg png webp) in den weißen Bereich ziehen (max. 100 MB/Datei)
        </div>
        <div class="flex-none space-x-2">
          <stormy-button
            type="button"
            @click="onClose"
          >
            Abbrechen
          </stormy-button>
          <stormy-button
            type="button"
            variant="primary"
            @click="onSave"
          >
            Medien auswählen
          </stormy-button>
        </div>
      </div>
    </template>
  </stormy-single-dialog>
</template>

<script setup>
import MediaModalCatalog from './_components/MediaModalCatalog.vue'
import MediaModalUpload from './_components/MediaModalUpload.vue'
import MediaDetails from './_components/MediaDetails.vue'
import MediaUploadButton from './_components/MediaUploadButton.vue'

import { useMediaStore } from '@/pinia/MediaStore'
import { storeToRefs } from 'pinia'
import { useProp } from '@/composables/useProp.js'
import useFileList from '@/composables/useFileList.js'

import { computed, onMounted, ref, toRefs, watch } from 'vue'
import createUploader from '@/composables/useFileUploader.js'

const mediaStore = useMediaStore()
const { isLoading, currentItem, currentMedia, selectedItems, mediaFiles } = storeToRefs(mediaStore)

const props = defineProps({
  folder: useProp(String),
  parent: useProp(String),
  parentId: useProp(Number),
  title: useProp(String, 'Medien auswählen'),
  multiple: useProp(Boolean, false),
  upload: useProp(Boolean, true),
  show: useProp(Boolean, true)
})

const { folder } = toRefs(props)
const tab = ref('base')

onMounted(async () => {
  mediaStore.setCurrentItem(0)
  await mediaStore.load(props.parent, props.parentId)
})

const onSelectNone = () => {
  selectedItems.value = []
}

const canSelectAll = computed(() => selectedItems.value.length !== mediaFiles.value.length)
const canSelectNone = computed(() => selectedItems.value.length)

const onSelectAll = () => {
  selectedItems.value = mediaFiles.value.map(item => item.id)
}

const { files, addFiles, removeFile } = useFileList()

watch(files, async (files) => {
  await uploadFiles(files)
})

function onInputChange (e) {
  addFiles(e.target.files)
  e.target.value = null // reset so that selecting the same file again will still cause it to fire this change
}

const { uploadFiles } = createUploader(props.parent, props.parentId)

const emit = defineEmits(['cancel', 'confirm'])

const onClose = () => {
  emit('cancel')
}

const onSave = () => {
  emit('confirm', selectedItems.value)
}

</script>
